import classNames from 'classnames';
import React, { HTMLProps, useCallback, useContext, useState } from 'react';
import { useIntl } from 'react-intl';
import css from './GlobalNav.module.scss';
import GlobalNavModalMenu from './GlobalNavModalMenu';
import UserMenu from './UserMenu';
import { LocaleContext } from '../../../contexts/LocaleContext';
import { useAuth } from '../../../hooks/api/auth';
import { useUserMe } from '../../../hooks/api/me';
import Link from 'next/link';
import { RSButton } from 'src/reservation-page/RSButton';
import { buildSiteUrl, buildUrl } from 'src/utils/request-url';

interface Props extends HTMLProps<{}> {
  logoImage: { url: string } | null;
  merchantPublicId: string | null;
  displayName: string | null;
}

const GlobalNav: React.FC<Props> = ({
  className,
  logoImage,
  merchantPublicId,
  displayName,
}) => {
  const intl = useIntl();
  const { user, isLoggedIn } = useUserMe();
  const { logout } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isActive, setIsActive] = useState<boolean>(false);
  const localeCtx = useContext(LocaleContext);

  const openModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const toggleUserMenu = useCallback((flag: boolean) => {
    setIsActive(flag);
  }, []);

  const intlPref = 'components.common.layouts.globalHeader.';

  return (
    <nav className={classNames(css.GlobalNav, className)}>
      <div className={css.GlobalNav__Inner}>
        {logoImage && merchantPublicId ? (
          <a href={buildSiteUrl()} className={css.GlobalNav__Logo}>
            <div>
              <img
                src={logoImage.url}
                alt="logo"
                className={css.GlobalNav__LogoImage}
              />
            </div>
          </a>
        ) : (
          <Link
            href={merchantPublicId ? buildSiteUrl('', 'next') : '/'}
            className={css.GlobalNav__DisplayName}
          >
            {displayName}
          </Link>
        )}

        <div className="flex items-center gap-1">
          <div className="flex items-center gap-0.5">
            <span>{intl.formatMessage({ id: intlPref + 'language' })}：</span>
            <select
              className="rounded-10 text-16 text-gray-1000 pc:text-15 border-gray-200 p-1"
              onChange={(e) => {
                localeCtx.changeLocale(e.target.value);
              }}
              value={localeCtx.currentLocale}
            >
              <option value="ja">日本語</option>
              <option value="en">English</option>
              <option value="ko">한국어</option>
            </select>
          </div>

          <div className={css.GlobalNav__Buttons}>
            {isLoggedIn && (
              <a href={buildUrl('/_')} className={css.GlobalNav__TextLink}>
                {intl.formatMessage({ id: intlPref + 'links.dashboard' })}
              </a>
            )}
            {isLoggedIn && user && (
              <UserMenu
                user={user}
                isActive={isActive}
                toggle={toggleUserMenu}
                logout={async () => {
                  await logout();
                  location.href = buildUrl(
                    `/signin/user?return_to=${location.pathname}`,
                  );
                }}
              />
            )}
            {!isLoggedIn && (
              <>
                <div className={css.GlobalNav__LoginButtonWrapper}>
                  <RSButton
                    variant="outline"
                    onClick={() => {
                      location.href = buildUrl(
                        `/signin/user?return_to=${location.pathname}`,
                      );
                    }}
                  >
                    {intl.formatMessage({ id: intlPref + 'buttons.signin' })}
                  </RSButton>
                </div>
                <svg className={css.GlobalNav__MenuButton} onClick={openModal}>
                  <use xlinkHref="#globalnav-menu" />
                </svg>
              </>
            )}
          </div>
        </div>
      </div>
      {isModalOpen && <GlobalNavModalMenu handleClose={closeModal} />}
    </nav>
  );
};

export default GlobalNav;
